import { useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Users, HandCoins, Megaphone, FileDown, Presentation, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Layout";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/guides`;
const GUIDES = [
  ["investor", "Investor Guide", "How investing works, adding and withdrawing money, worked example on K5,000 and benefits.", TrendingUp],
  ["village", "Village Banking Guide", "Saving monthly, shares, borrowing from your group and sharing profit — example on K200 a month.", Users],
  ["borrowing", "Borrowing Guide", "How to apply, loan levels, full cost and repayment plan — example on a K2,000 loan.", HandCoins],
  ["agents", "Credit Agent Guide", "Becoming a credit agent, invite links, client onboarding and how commission is earned.", Megaphone],
];

function DownloadBtn({ id, fmt, label, Icon, primary }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BASE}/${id}.${fmt}`);
      if (!r.ok) throw new Error(`Server replied ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const name = (r.headers.get("content-disposition") || "").match(/filename="(.+)"/)?.[1] || `Tiyeni-Capital-${id}-guide.${fmt}`;
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Guide downloaded", { description: name });
    } catch (e) {
      toast.error("Download failed", { description: `${e.message}. Please try again.` });
    } finally { setBusy(false); }
  };
  return (
    <Button onClick={go} disabled={busy} variant={primary ? "default" : "outline"} className={primary ? "bg-[#0369a1]" : ""} data-testid={`guide-${id}-${fmt}`}>
      {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Icon className="mr-1.5 h-4 w-4" />}{busy ? "Preparing…" : label}
    </Button>
  );
}

export function GuidesList() {
  return (
    <div className="grid gap-5 md:grid-cols-2" data-testid="guides-list">
      {GUIDES.map(([id, title, desc, Icon]) => (
        <div key={id} className="flex flex-col rounded-2xl border border-stone-200 bg-white p-6 lift" data-testid={`guide-card-${id}`}>
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#0369a1]/10"><Icon className="h-6 w-6 text-[#0369a1]" /></div>
          <h3 className="mt-4 font-heading text-xl font-semibold text-brand">{title}</h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-stone-600">{desc}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <DownloadBtn id={id} fmt="pdf" label="Download PDF" Icon={FileDown} primary />
            <DownloadBtn id={id} fmt="pptx" label="PowerPoint" Icon={Presentation} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function GuidesIntro() {
  return <p className="max-w-2xl text-sm text-stone-600" data-testid="guides-intro">Step-by-step guides with screenshots, diagrams and worked examples. Rates, fees and rules in every guide are taken from the current settings each time you download, so they are always up to date.</p>;
}

export function GuidesInApp() {
  return (
    <div className="space-y-6" data-testid="guides-page">
      <div><div className="overline mb-2">Guides</div><h1 className="font-heading text-3xl font-bold text-brand">How Tiyeni Capital works</h1></div>
      <GuidesIntro />
      <GuidesList />
    </div>
  );
}

export default function GuidesPublic() {
  return (
    <div className="min-h-screen bg-sky-50/50" data-testid="guides-public-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <Logo />
        <Button asChild variant="ghost" data-testid="guides-back-home"><Link to="/"><ArrowLeft className="mr-1.5 h-4 w-4" />Home</Link></Button>
      </header>
      <main className="mx-auto max-w-5xl space-y-6 px-5 pb-20 pt-6">
        <div className="overline">Guides</div>
        <h1 className="font-heading text-4xl font-bold text-brand sm:text-5xl">Learn how Tiyeni Capital works</h1>
        <GuidesIntro />
        <GuidesList />
      </main>
    </div>
  );
}
